import type { Store } from '@reduxjs/toolkit'
import type { AxiosProgressEvent, GenericAbortSignal } from 'axios'
import type {
    ChangesAndAsyncUploadStatus,
    DeletedFile,
    ExtraJobQueryParamsOf,
    FileGroupType,
    JobInfoResponse,
    StoryJobResponse,
    UploadResponse,
} from '~/@types/backend-types'
import { trackEventInternal } from '~/analytics/eventTracking'
import { queryCollectionByIDWithCache, queryTimelineJobIDWithCache } from '~/query/app.queries'
import { queryJobInfo, QueryJobInfoError } from '~/query/job.queries'
import { AlbumDetailsFetched } from '~/state/album/actions'
import { BulkOfActions, type Dispatch } from '~/state/common/actions'
import {
    ConnectedDeviceWasDeleted,
    ConnectedDevicesWasFetched,
    DeleteConnectedDeviceFailed,
    DeleteConnectedDeviceStarted,
    FetchingConnectedDevicesFailed,
    FetchingConnectedDevicesStarted,
    JobSubscriptionsDetected,
    UserSubscribedToAlbum,
} from '~/state/currentUser/actions'
import type { FileMetadata } from '~/state/fileMetadata/actions'
import {
    FileMetadataFetchingFailed,
    FileMetadataFetchingStarted,
    FileMetadataWasFetched,
} from '~/state/fileMetadata/actions'
import * as FilesActions from '~/state/files/actions'
import { FileObjectsWereFetched } from '~/state/files/actions'
import type { ExtendedJobFile } from '~/state/files/reducer'
import type { FileWithGrouping } from '~/state/files/selectors'
import type {
    FileCopiedInfo,
    FilesFetchedPayload,
    FilesRestorationFailedPayload,
    FilesRestorationSucceededPayload,
} from '~/state/job/actions'
import {
    FetchedDefaultJob,
    FetchedLastSerialOfDefaultJob,
    FileRangeFetchFailed,
    FileRangeFetchingStarted,
    FileRangeWasFetched,
    FileWasCopiedToJob,
    FileWasRemovedFromJob,
    FilesCopiedToAlbum,
    FilesCopiedToAlbumFailed,
    FilesDeletionFailed,
    FilesDeletionStarted,
    FilesDeletionSucceeded,
    FilesRestorationFailed,
    FilesRestorationStarted,
    FilesRestorationSucceeded,
    JobCopiedToTimeline,
    JobCopiedToTimelineFailed,
    JobPublishingFailed,
    JobWasPublished,
    ShareCreationFailed,
    ShareWasCreated,
    StartFetchingDefaultJob,
    UnableToFetchDefaultJob,
    UnableToFetchJobChanges,
} from '~/state/job/actions'
import {
    ErrorWhenFetchingJobInfo,
    FetchedJobInfo,
    type JobInfoReference,
    JobListWasFetched,
    JobNotFound,
    JobRequiresLoggedInUser,
    JobRequiresPassword,
    StartedFetchingJobInfo,
} from '~/state/jobInfo/actions'
import { JobInfoStatus, type JobInfo } from '~/state/jobInfo/reducer'
import { jobFilesToCaptureFiles } from '~/state/recentFiles/recentFilesProcessing'
import {
    RecentFilesStatus,
    addRecentFiles,
    updateRecentsError,
    updateRecentsStatus,
} from '~/state/recentFiles/recentFilesSlice'
import { ShareCopiedToTimeline, ShareCopiedToTimelineFailed } from '~/state/share/actions'
import {
    LongRunningTaskFinished,
    LongRunningTaskStarted,
} from '~/state/statusNotifications/actions'
import { SubscribersWereFetched } from '~/state/subscribers/actions'
import { TimelineMonthsFetched } from '~/state/timeline/actions'
import {
    FetchRemainingTrashFilesProgressUpdated,
    RestoreAllProgressUpdated,
    TrashFileDeleteFailed,
    TrashFileDeleted,
    TrashFilesDeletionFailed,
    TrashFilesDeletionStarted,
    TrashFilesDeletionSucceeded,
    TrashLoadingFailed,
    TrashLoadingStarted,
    TrashLoadingSucceeded,
} from '~/state/trash/actions'
import { FileUploadSucceeded, UploaderFinished } from '~/state/uploader/actions'
import type { FileInformation } from '~/state/uploader/reducer'
import {
    getBackendSucceededFileIDByFileUUID,
    getSucceededFiles,
    isUploaderDone,
} from '~/state/uploader/selectors'
import type { UploadDecorator, UploadMethod } from '~/state/uploader/uploadQueue'
import { UserInfoWasFetched } from '~/state/users/actions'
import { getRange, uniqueArray, withoutTheUndefined } from '~/utilities/arrayUtils'
import { managedPromiseAll } from '~/utilities/promises'

import type {
    CollectionDTO,
    GetRequestJobsFilesByJobUuidParams,
    FileActionErrorReasonDTO,
    TimelineMonthDTO,
} from '@capture/client-api/src/orval'
import { FileActionStatusDTO, UploadPolicyDTO } from '@capture/client-api/src/orval'

import { getUserDefinedViewMode } from './album'
import { getAppService } from './SessionServiceProvider'
import { getUploadSyncer } from './syncers/UploadSyncer'

export async function fetchDefaultJobID(dispatch: Dispatch): Promise<JobID> {
    try {
        dispatch(StartFetchingDefaultJob())
        const jobInfo = await getAppService().getDefaultJob()
        dispatch(FetchedLastSerialOfDefaultJob(jobInfo.last_update ?? -1))
        dispatch(FetchedDefaultJob(jobInfo))
        return jobInfo.id
    } catch (err) {
        dispatch(UnableToFetchDefaultJob())
        throw err
    }
}

export const fetchTimelineMonthsFiles = async (
    dispatch: Dispatch,
    timelineID: JobID,
    recursive?: boolean,
): Promise<void> => {
    const months = await fetchTimelineMonths(dispatch, timelineID)

    const sortedMonths = months.sort((a, b) => b.year - a.year || b.month - a.month)

    sortedMonths.forEach(({ year, month }) => {
        void fetchTimelineFileRange(
            dispatch,
            timelineID,
            { year, month, day: 1 },
            undefined,
            recursive,
        )
    })
}

export const fetchTimelineMonths = async (
    dispatch: Dispatch,
    jobID: JobID,
): Promise<TimelineMonthDTO[]> => {
    const service = getAppService()
    const { months } = await service.getTimelineMonths(jobID)
    dispatch(TimelineMonthsFetched({ jobID, months }))
    return months
}

export const initTimeline = async (dispatch: Dispatch) => {
    const timelineID = await fetchDefaultJobID(dispatch)
    await fetchTimelineMonths(dispatch, timelineID)
    return timelineID
}

export const copyAlbumFilesToTimeline = async (
    dispatch: Dispatch,
    albumID: JobID,
    fileIDs: FileID[],
): Promise<void> => {
    try {
        dispatch(LongRunningTaskStarted('filesAreBeingCopied'))

        const service = getAppService()
        await service.copyFilesToDefaultJob(albumID, fileIDs)
        dispatch(FilesActions.FilesCopiedToTimeline(fileIDs))
    } catch (error: any) {
        const reason =
            error.response && error.response.status === 413 ? 'out_of_storage' : 'unknown'
        dispatch(FilesActions.FilesCopiedToTimelineFailed({ reason }))
    }
    dispatch(LongRunningTaskFinished('filesAreBeingCopied'))
}

export const copyShareToTimeline = async (dispatch: Dispatch, jobID: JobID): Promise<void> => {
    try {
        dispatch(LongRunningTaskStarted('filesAreBeingCopied'))
        const service = getAppService()
        await service.copyJobToDefaultJob(jobID)
        dispatch(ShareCopiedToTimeline())
    } catch (e) {
        console.error('Failed to copy share to timeline:', e)
        dispatch(ShareCopiedToTimelineFailed())
    }

    dispatch(LongRunningTaskFinished('filesAreBeingCopied'))
}

export const copyAlbumToTimeline = async (dispatch: Dispatch, jobID: JobID): Promise<void> => {
    try {
        dispatch(LongRunningTaskStarted('filesAreBeingCopied'))
        const service = getAppService()
        await service.copyJobToDefaultJob(jobID)
        dispatch(JobCopiedToTimeline())
    } catch (error: any) {
        const reason =
            error.response && error.response.status === 413 ? 'out_of_storage' : 'unknown'
        dispatch(JobCopiedToTimelineFailed({ reason }))
    }

    dispatch(LongRunningTaskFinished('filesAreBeingCopied'))
}

export const copyMultipleFilesToJob = async (
    dispatch: Dispatch,
    jobID: JobID,
    files: Array<FileWithGrouping<ExtendedJobFile>>,
): Promise<{ succeeded: FileID[]; failed: FileID[]; errors?: Error[] }> => {
    const successFileInfos: FileCopiedInfo[] = []
    const succeeded: FileID[] = []
    const failed: FileID[] = []
    const errors: Error[] = []
    const service = getAppService()

    const associatedFiles: ExtendedJobFile[] = []
    files.forEach((f) => {
        if (f.livePhotoFile) {
            associatedFiles.push(f.livePhotoFile)
        }
        if (f.burstFiles) {
            associatedFiles.push(...f.burstFiles)
        }
    })

    await managedPromiseAll(
        [...files, ...associatedFiles],
        async ({ fileID, path, checksum, mtime, ctime, group }) => {
            const uuid = await service
                .dedupFile(jobID, {
                    path: path.split('/').pop()!, // Extract last segment of path (remove folders, CAPWEB-1172)
                    checksum,
                    mtime,
                    ctime,
                    policy: UploadPolicyDTO.no_duplicates,
                    group_id: group?.id,
                    group_type: group?.type as FileGroupType,
                    master: group && group.isMaster,
                })
                .catch((e: Error) => {
                    errors.push(e)
                    return undefined
                })
            if (uuid !== undefined) {
                successFileInfos.push({
                    from: fileID,
                    to: { jobID, fileID: uuid },
                })
                if (!group || group?.isMaster) {
                    succeeded.push(fileID)
                }
            } else {
                failed.push(fileID)
            }
        },
    )

    if (successFileInfos.length > 0) {
        dispatch(BulkOfActions(successFileInfos.map(FileWasCopiedToJob)))
    }

    return { succeeded, failed, errors }
}

export const tryCopyFilesToJobCompletely = async (
    dispatch: Dispatch,
    jobID: JobID,
    files: ExtendedJobFile[],
): Promise<FileID[]> => {
    const { succeeded, failed } = await copyMultipleFilesToJob(dispatch, jobID, files)
    if (failed.length > 0) {
        throw Error()
    }

    return succeeded
}

export const copyFilesToAlbum = async (
    dispatch: Dispatch,
    jobID: JobID,
    files: Array<FileWithGrouping<ExtendedJobFile>>,
): Promise<void> => {
    dispatch(LongRunningTaskStarted('filesAreBeingCopied'))

    const { succeeded, failed } = await copyMultipleFilesToJob(dispatch, jobID, files)
    dispatch(
        BulkOfActions(
            withoutTheUndefined([
                LongRunningTaskFinished('filesAreBeingCopied'),
                succeeded.length > 0
                    ? FilesCopiedToAlbum({
                          jobID,
                          files: succeeded,
                          showAlbumLink: true,
                      })
                    : undefined,
                failed.length > 0 ? FilesCopiedToAlbumFailed({ jobID, files: failed }) : undefined,
            ]),
        ),
    )
}

const fetchTimelineFiles = async (
    jobID: JobID,
    options: ExtraJobQueryParamsOf<GetRequestJobsFilesByJobUuidParams>,
): Promise<FilesFetchedPayload> => {
    const service = getAppService()
    const result = await service.getFiles(jobID, options)
    return {
        jobID,
        lastEvent: result.lastEventSerial,
        files: result.files.map((f) => ({
            ...f,
            jobID,
            fileID: f.id,
            user_uuid: 'currentUser',
        })),
    }
}

function getFileRangeOptions(start: DayRef, end: DayRef) {
    return {
        start: `${start.year}/${start.month}/${start.day}`,
        end: `${end.year}/${end.month}/${end.day + 1}`, // Add extra day since backend uses 00:00 as cutoff
    }
}

export type FetchFileRangeMethod = (
    dispatch: Dispatch,
    jobID: JobID,
    start: DayRef,
    end?: DayRef,
    recursive?: boolean,
) => Promise<void>
export const fetchTimelineFileRange: FetchFileRangeMethod = async (
    dispatch,
    jobID,
    start,
    end,
    recursive,
) => {
    end = end ?? { ...start, day: 31 }
    try {
        const options = { ...getFileRangeOptions(start, end), recursive }
        dispatch(FileRangeFetchingStarted({ jobID, start, end }))
        const response = await fetchTimelineFiles(jobID, options)
        dispatch(FileRangeWasFetched({ ...response, start, end }))
    } catch (e) {
        console.error('Failed to fetch file range:', e)
        dispatch(FileRangeFetchFailed({ jobID, start, end }))
    }
}

export async function getTimelineFilesByIds(dispatch: Dispatch, fileIDs: FileID[]) {
    try {
        const timelineID = await queryTimelineJobIDWithCache()
        const files = await getAppService().getFilesByIDs(timelineID, fileIDs)
        dispatch(
            FileObjectsWereFetched(
                files.map((f) => ({
                    ...f,
                    jobID: timelineID,
                    fileID: f.id,
                    user_uuid: 'currentUser',
                })),
            ),
        )
        return files
    } catch (e) {
        console.error('Failed to get timeline files by IDs:', e)
    }
}

export async function fetchCollectionWithFiles(
    dispatch: Dispatch,
    collectionID: string,
): Promise<CollectionDTO> {
    const collection = await queryCollectionByIDWithCache(collectionID)
    await getTimelineFilesByIds(dispatch, collection.photos)

    return collection
}

export const fetchAllUserFiles = async (dispatch: Dispatch, timelineID: JobID) => {
    return fetchTimelineMonthsFiles(dispatch, timelineID, true)
}
function getFormattedNowDate() {
    const now = new Date()

    const year = now.getFullYear()
    const month = String(now.getMonth() + 1).padStart(2, '0') // getMonth() is zero-based
    const day = String(now.getDate()).padStart(2, '0')

    return `${year}/${month}/${day}`
}

export const fetchAllRecentFiles = async (dispatch: Dispatch) => {
    try {
        const start = new Date().getTime()
        const app = getAppService()
        const timelineID = await app.getDefaultJob()
        const queryRecentFilesOptions = {
            recursive: true,
            uploaded_since: 30,
            today: getFormattedNowDate(),
        }

        dispatch(updateRecentsStatus(RecentFilesStatus.FETCHING))
        const result = await getAppService().getFiles(timelineID.id, queryRecentFilesOptions)

        const captureFiles = jobFilesToCaptureFiles(timelineID.id, 'currentUser', result.files)

        dispatch(
            BulkOfActions([
                addRecentFiles(captureFiles),
                updateRecentsStatus(RecentFilesStatus.SUCCEEDED),
            ]),
        )
        const end = new Date().getTime()
        trackEventInternal('recents_runtime', {
            time: end - start,
            filesCount: captureFiles.length,
        })
    } catch (error) {
        dispatch(updateRecentsStatus(RecentFilesStatus.FAILED))
        if (typeof error === 'string') {
            dispatch(updateRecentsError(error))
        } else if (error instanceof Error) {
            dispatch(updateRecentsError(error.message))
        }
    }
}

export const fetchListOfJobs = async (
    dispatch: Dispatch,
    currentUserID: UserID,
): Promise<StoryJobResponse[] | undefined> => {
    try {
        const service = getAppService()
        const jobInfos = await service.getJobList()
        const albumInfos = jobInfos.filter((i): i is StoryJobResponse => i.type === 'story')

        const jobInfoReferences = albumInfos.map((info): JobInfoReference => {
            // Backend provides permissions combined into bitmap when fetching list of jobs
            const hasPerm = (flag: number) => (info.permissions & flag) === flag
            return {
                job: info.id,
                info: {
                    type: info.type,
                    ctime: info.ctime,
                    mtime: info.mtime,
                    owner: info.owner.uuid,
                    title: info.name,
                    allow_comments: hasPerm(1),
                    allow_uploads: hasPerm(2),
                    coverPhoto: info.cover_id,
                    isShared: info.privacy_mode === 'shared',
                    last_update: info.last_update,
                    sort_order: info.attributes?.sort_order,
                    size: info.size,
                    has_heic: info.has_heic,
                },
            }
        })

        const detailsFetchedActions = albumInfos.map((info) => {
            const hasPerm = (flag: number) => (info.permissions & flag) === flag
            return AlbumDetailsFetched({
                albumID: info.id,
                title: info.name,
                owner: {
                    userID: info.owner.uuid,
                    name: info.owner.name || '',
                    email: info.owner.email,
                    profilePicture: info.owner.profile_picture_url,
                },
                coverPhotoID: info.cover_id,
                ctime: info.ctime,
                mtime: info.mtime,
                allow_comments: hasPerm(1),
                allow_uploads: hasPerm(2),
                numberOf: {
                    contributors: info.participant_count,
                    files: info.media_count,
                    comments: info.comment_count,
                    loves: info.reaction_count,
                },
                isShared: info.privacy_mode === 'shared',
                sort_order: info.attributes?.sort_order,
                size: info.size,
                hasHEIC: info.has_heic,
            })
        })
        const jobSubscriptionsAction = JobSubscriptionsDetected(
            albumInfos.filter(({ owner }) => owner.uuid !== currentUserID).map(({ id }) => id),
        )

        dispatch(
            BulkOfActions([
                JobListWasFetched(jobInfoReferences),
                ...detailsFetchedActions,
                jobSubscriptionsAction,
            ]),
        )

        return albumInfos
    } catch (e) {
        console.error('Failed to fetch list of jobs:', e)
        dispatch(UnableToFetchDefaultJob())
        return undefined
    }
}

export const fetchContributingUsers = async (
    dispatch: Dispatch,
    jobID: JobID,
    currentUserID?: string,
): Promise<void> => {
    const ah = getAppService()

    const response = await ah.getJobContributors(jobID, true)
    // TODO: refactor subscribers -> participants. Remove subscriptions from current user
    if (
        currentUserID &&
        response.users[currentUserID] &&
        response.users[currentUserID].subscribed === true
    ) {
        dispatch(UserSubscribedToAlbum(jobID))
    }
    const users = Object.keys(response.users).map((uuid) => ({
        userID: uuid,
        name: response.users[uuid].name,
        profilePicture: response.users[uuid].profile_picture_url as string | undefined,
    }))
    dispatch(UserInfoWasFetched(users))
    dispatch(SubscribersWereFetched({ jobID, subscribers: users }))
}

/* Provide a `uploadFile`-method that allows uploads to some other job than timeline to be kept on timeline too */
export const uploaderWithTimelineMirroring =
    (store: Store, dispatch: Dispatch, getDefaultJobID: () => JobID | undefined): UploadDecorator =>
    (upload: UploadMethod): UploadMethod =>
    async (
        file: File,
        info: FileInformation,
        onUploadProgress: (event: AxiosProgressEvent) => void,
        signal: GenericAbortSignal,
    ) => {
        const timelineID = getDefaultJobID()
        const uploadResponse = await upload(file, info, onUploadProgress, signal)
        if (uploadResponse.content.uuid && timelineID && info.targetJob !== timelineID) {
            await handleAsyncUploadWithPoller(uploadResponse, timelineID, info.targetJob)
        }

        async function handleAsyncUploadWithPoller(
            response: UploadResponse,
            timelineID: JobID,
            targetID: JobID,
        ) {
            function onBackendUploadSuccess(fileID: FileID, used_space: number) {
                const state = store.getState()
                // This FileID is an index id in redux store which is used for Uploader state
                const getBackendSucceededFileID = getBackendSucceededFileIDByFileUUID(state, fileID)

                if (typeof getBackendSucceededFileID === 'number') {
                    dispatch(
                        FileUploadSucceeded({
                            fileID: getBackendSucceededFileID,
                            fileUUID: fileID,
                            usedStorage: used_space,
                        }),
                    )
                    if (isUploaderDone(state)) {
                        dispatch(
                            UploaderFinished({
                                filesCount: getSucceededFiles(state).length,
                            }),
                        )
                    }
                }
            }

            async function copyToTimeline(timelineID: JobID, targetID: JobID, fileID: FileID) {
                try {
                    const mirrorService = getAppService()
                    await mirrorService.copyFilesToDefaultJob(targetID, [fileID])
                } catch (e) {
                    console.error('Failed to copy file to timeline:', e)
                }
            }

            function errorHandler() {
                store.dispatch(UnableToFetchJobChanges(targetID))
            }

            getUploadSyncer().watchFile(
                targetID,
                response.content.uuid,
                (processResponse: ChangesAndAsyncUploadStatus) => {
                    if (info.alsoTargetTimeline) {
                        void copyToTimeline(timelineID, targetID, response.content.uuid)
                    }
                    // TODO CAPWEB-3182: we need to handle the case where the file exists
                    onBackendUploadSuccess(
                        response.content.uuid,
                        processResponse.info.used_space, // if the file exists, there is no info object
                    )
                },
                errorHandler,
            )
        }
        return uploadResponse
    }

export const uploadFile: UploadMethod = async (
    f: File,
    i: FileInformation,
    onUploadProgress: (event: AxiosProgressEvent) => void,
    signal: GenericAbortSignal,
) => {
    const path = i.targetFolder + '/' + f.name
    const mtime = Math.floor((f.lastModified || Date.now()) / 1000)
    const ah = getAppService()
    return ah.uploadFile(i.targetJob, path, f, mtime, {
        onUploadProgress,
        signal,
    })
}
export const deleteTrashFile = async (dispatch: Dispatch, fileID: FileID): Promise<void> => {
    dispatch(FilesActions.FileDeletionStarted(fileID))
    try {
        const service = getAppService()
        await service.emptyTrashCan(fileID)
        dispatch(TrashFileDeleted(fileID))
    } catch (e) {
        console.error('Failed to delete trash file:', e)
        dispatch(TrashFileDeleteFailed(fileID))
    }
}

export const deleteMultipleTrashFiles = async (
    dispatch: Dispatch,
    files: FileID[],
): Promise<void> => {
    try {
        dispatch(TrashFilesDeletionStarted(files))
        dispatch(LongRunningTaskStarted('filesAreBeingDeleted'))
        const service = getAppService()
        const successFiles: FileID[] = []
        const failedFiles: FileID[] = []

        await managedPromiseAll(files, async (fileID) => {
            try {
                await service.emptyTrashCan(fileID)
                successFiles.push(fileID)
            } catch (e) {
                console.error('Failed to delete trash file:', e)
                failedFiles.push(fileID)
            }
        })

        const actions = withoutTheUndefined([
            LongRunningTaskFinished('filesAreBeingDeleted'),
            successFiles.length > 0 ? TrashFilesDeletionSucceeded(successFiles) : undefined,
            failedFiles.length > 0 ? TrashFilesDeletionFailed(failedFiles) : undefined,
        ])

        dispatch(BulkOfActions(actions))
    } catch (e) {
        console.error('Failed to delete multiple trash files:', e)
    }
}

export const deleteFile = async (
    dispatch: Dispatch,
    jobID: JobID,
    fileID: FileID,
): Promise<void> => {
    try {
        dispatch(FilesActions.FileDeletionStarted(fileID))
        const service = getAppService()
        const resp = await service.deleteFile(jobID, fileID)
        dispatch(
            BulkOfActions([
                FileWasRemovedFromJob({ jobID, fileID }),
                FilesDeletionSucceeded({
                    jobID,
                    files: [fileID],
                    usedSpace: resp ? resp.used_space : undefined,
                }),
            ]),
        )
    } catch (error) {
        const supressRetry = (error as Error)?.message === 'Forbidden'

        dispatch(
            BulkOfActions([
                FilesActions.FileDeletionFailed(fileID),
                FilesDeletionFailed({
                    jobID,
                    files: [fileID],
                    supressRetry,
                }),
            ]),
        )
    }
}

export const deleteMultipleFiles = async (
    dispatch: Dispatch,
    jobID: JobID,
    files: FileID[],
): Promise<void> => {
    try {
        dispatch(FilesDeletionStarted({ jobID, files }))
        dispatch(LongRunningTaskStarted('filesAreBeingDeleted'))

        const service = getAppService()
        const successFiles: FileID[] = []
        const failedFiles: FileID[] = []

        await managedPromiseAll(files, async (fileID) => {
            try {
                await service.deleteFile(jobID, fileID)
                successFiles.push(fileID)
            } catch (e) {
                console.error('Failed to delete file:', e)
                failedFiles.push(fileID)
            }
        })

        const sortedSuccessFiles = files.filter((fileID) => successFiles.includes(fileID))

        const actions = withoutTheUndefined([
            ...sortedSuccessFiles.map((fileID) => FileWasRemovedFromJob({ jobID, fileID })),
            ...failedFiles.map((fileID) => FilesActions.FileDeletionFailed(fileID)),
            LongRunningTaskFinished('filesAreBeingDeleted'),
            sortedSuccessFiles.length > 0
                ? FilesDeletionSucceeded({
                      jobID,
                      files: sortedSuccessFiles,
                  })
                : undefined,
            failedFiles.length > 0 ? FilesDeletionFailed({ jobID, files: failedFiles }) : undefined,
        ])

        dispatch(BulkOfActions(actions))
    } catch (e) {
        console.error('Failed to delete multiple files:', e)
    }
}

const _restoreMultipleFilesWithResults = async (
    jobID: JobID,
    files: FileID[],
    onProgress?: (progress: number) => void,
): Promise<{
    successFilesResult?: FilesRestorationSucceededPayload
    failedFilesResult?: FilesRestorationFailedPayload
}> => {
    const service = getAppService()

    const { results, quota } = await service.restoreMultipleFiles(jobID, files, onProgress)

    const successResults = results.filter((result) => result.status === FileActionStatusDTO.ok)
    const failedResults = results.filter((result) => result.status === FileActionStatusDTO.error)

    return {
        successFilesResult:
            successResults.length > 0
                ? {
                      jobID,
                      files: successResults.map((s) => s.file_uuid),
                      usedSpace: quota?.used_space,
                  }
                : undefined,
        failedFilesResult:
            failedResults.length > 0
                ? {
                      jobID,
                      files: failedResults.map((f) => f.file_uuid),
                      reasons: uniqueArray(
                          failedResults
                              .map((f) => f.error_reason)
                              .filter((r): r is FileActionErrorReasonDTO => r !== undefined),
                      ),
                  }
                : undefined,
    }
}

export const restoreMultipleFiles = async (
    dispatch: Dispatch,
    jobID: JobID,
    files: FileID[],
    onProgress?: (progress: number) => void,
): Promise<void> => {
    try {
        dispatch(FilesRestorationStarted({ jobID, files }))
        dispatch(LongRunningTaskStarted('filesAreBeingRestored'))

        const { successFilesResult, failedFilesResult } = await _restoreMultipleFilesWithResults(
            jobID,
            files,
            onProgress,
        )

        const actions = withoutTheUndefined([
            LongRunningTaskFinished('filesAreBeingRestored'),
            successFilesResult ? FilesRestorationSucceeded(successFilesResult) : undefined,
            failedFilesResult ? FilesRestorationFailed(failedFilesResult) : undefined,
        ])

        dispatch(BulkOfActions(actions))
    } catch (e) {
        console.error('Failed to restore multiple files:', e)
    }
}

export const fetchFileMetadata = async (
    dispatch: Dispatch,
    jobID: JobID,
    fileID: FileID,
): Promise<void> => {
    dispatch(FileMetadataFetchingStarted(fileID))
    try {
        const service = getAppService()
        const response = await service.getFileMetadata(jobID, fileID)
        const payload: FileMetadata = {
            fileID,
            deviceManufacturer: response.Make,
            deviceModel: response.Model,
            iso: response.ISOSpeedRatings,
            aperture: response.ApertureValue,
            exposure: response.ExposureTime,
            focalLength: response.FocalLength,
        }

        dispatch(FileMetadataWasFetched(payload))
    } catch (e) {
        console.error('Failed to fetch file metadata:', e)
        dispatch(FileMetadataFetchingFailed(fileID))
    }
}

export const fetchConnectedDevices = async (dispatch: Dispatch): Promise<void> => {
    dispatch(FetchingConnectedDevicesStarted())
    try {
        const service = getAppService()
        const response = await service.getConnectedDevices()

        dispatch(
            ConnectedDevicesWasFetched({
                currentDeviceID: response.current,
                devices: response.devices,
            }),
        )
    } catch (e) {
        console.error('Failed to fetch connected devices:', e)
        dispatch(FetchingConnectedDevicesFailed())
    }
}
export const removeConnectedDevice = async (
    dispatch: Dispatch,
    deviceID: string,
): Promise<void> => {
    dispatch(DeleteConnectedDeviceStarted({ deviceID }))
    try {
        const service = getAppService()
        await service.deleteConnectedDevice(deviceID)
        dispatch(ConnectedDeviceWasDeleted({ deviceID }))
    } catch (e) {
        console.error('Failed to delete connected device:', e)
        dispatch(DeleteConnectedDeviceFailed({ deviceID }))
    }
}

export const createShareWithFiles = async (
    dispatch: Dispatch,
    name: string,
    files: ExtendedJobFile[],
    password?: string,
): Promise<JobID | undefined> => {
    try {
        const service = getAppService()
        const { id } = await service.createJob({
            name,
            public: true,
            password,
        })
        await tryCopyFilesToJobCompletely(dispatch, id, files)
        dispatch(ShareWasCreated(id))
        return id
    } catch (e) {
        console.error('Failed to create share with files:', e)
        dispatch(ShareCreationFailed())
    }
}

export const getSupportedExtensions = async () => {
    const service = getAppService()

    try {
        const extensions = await service.getSupportedExtensions()
        return extensions
    } catch (e) {
        console.error('Failed to get supported extensions:', e)
    }
}

export const publishJobByEmail = async (
    dispatch: Dispatch,
    jobID: JobID,
    toEmail: string,
    subject: string,
    message?: string,
): Promise<void> => {
    try {
        const service = getAppService()
        await service.publishJob(jobID, {
            to_email: toEmail,
            subject,
            message,
        })
        dispatch(JobWasPublished(jobID))
    } catch (e) {
        console.error('Failed to publish job by email:', e)
        dispatch(JobPublishingFailed(jobID))
    }
}

export const updateTrashContent = async (
    dispatch: Dispatch,
    latestOffset: number,
    limit: number,
) => {
    dispatch(TrashLoadingStarted())
    try {
        const service = getAppService()
        const trashContentResponses = await managedPromiseAll(
            getRange(latestOffset / limit + 1),
            (pageIndex) => {
                return service.getDeletedFiles(pageIndex * limit, limit)
            },
        )

        dispatch(
            BulkOfActions(
                trashContentResponses.map((r, index) =>
                    TrashLoadingSucceeded({
                        ...r,
                        offset: index * limit,
                    }),
                ),
            ),
        )
    } catch (e) {
        console.error('Failed to update trash content:', e)
        dispatch(TrashLoadingFailed())
    }
}

export const loadTrashContent = async (dispatch: Dispatch, offset: number, limit: number) => {
    dispatch(TrashLoadingStarted())
    try {
        const service = getAppService()
        const trashContent = await service.getDeletedFiles(offset, limit)

        dispatch(TrashLoadingSucceeded({ ...trashContent, offset }))
    } catch (e) {
        console.error('Failed to load trash content:', e)
        dispatch(TrashLoadingFailed())
    }
}

const _recursivelyFetchRemainingDeletedFiles = async (
    dispatch: Dispatch,
    offset: number,
    itemsLimit: number,
    accFiles: DeletedFile[],
    onStepDone: () => void,
): Promise<DeletedFile[]> => {
    const service = getAppService()
    const { deletedFiles, totalItemCount } = await service.getDeletedFiles(offset, itemsLimit)
    const resultItemCount = deletedFiles.length

    dispatch(
        TrashLoadingSucceeded({
            deletedFiles,
            totalItemCount,
            offset,
        }),
    )

    if (onStepDone) {
        onStepDone()
    }

    if (offset + resultItemCount === totalItemCount) {
        return accFiles.concat(deletedFiles)
    }

    return _recursivelyFetchRemainingDeletedFiles(
        dispatch,
        offset + itemsLimit,
        itemsLimit,
        accFiles.concat(deletedFiles),
        onStepDone,
    )
}

export const fetchRemainingDeletedFiles = async (
    dispatch: Dispatch,
    fetchedTrashFileCount: number,
    totalItemCount: number,
    limit: number,
) => {
    dispatch(FetchRemainingTrashFilesProgressUpdated(0))

    if (totalItemCount === fetchedTrashFileCount) {
        dispatch(FetchRemainingTrashFilesProgressUpdated(1))
        return []
    }

    let remainingFiles: DeletedFile[] = []
    const latestOffset = Math.ceil(fetchedTrashFileCount / limit)
    const nextOffset = latestOffset * limit
    const remainingFetchCount = Math.ceil((totalItemCount - nextOffset) / limit)

    let finished = 0
    try {
        dispatch(TrashLoadingStarted())
        remainingFiles = await _recursivelyFetchRemainingDeletedFiles(
            dispatch,
            nextOffset,
            limit,
            [],
            () => {
                finished += 1

                dispatch(FetchRemainingTrashFilesProgressUpdated(finished / remainingFetchCount))
            },
        )
        return remainingFiles
    } catch (error) {
        dispatch(TrashLoadingFailed())
        throw error
    }
}

export const restoreAllTrashFiles = async (
    dispatch: Dispatch,
    options: {
        timelineID: JobID
        limit: number
        totalItemCount: number
        fetchedTrashFileIDs: FileID[]
    },
) => {
    const { timelineID, totalItemCount, fetchedTrashFileIDs, limit } = options
    const remainingFiles = await fetchRemainingDeletedFiles(
        dispatch,
        fetchedTrashFileIDs.length,
        totalItemCount,
        limit,
    )
    const allTrashFileIDs = fetchedTrashFileIDs.concat(remainingFiles.map((f) => f.id))

    dispatch(RestoreAllProgressUpdated(0))
    return _restoreMultipleFilesWithResults(timelineID, allTrashFileIDs, (progress) => {
        dispatch(RestoreAllProgressUpdated(progress))
    })
}

export type FetchJobInfoResult = {
    status: JobInfoStatus
    jobInfo: JobInfoResponse
}

export class FetchJobInfoError extends Error {
    status: JobInfoStatus

    constructor(status: JobInfoStatus, cause?: unknown) {
        super('Failed to fetch job info')
        this.status = status
        if (cause instanceof Error) {
            this.cause = cause
        }
    }
}

export async function fetchJobInfo(
    dispatch: Dispatch,
    jobID: JobID,
    currentUserID?: UserID,
): Promise<FetchJobInfoResult> {
    try {
        dispatch(StartedFetchingJobInfo(jobID))

        // Use queryJobInfo which handles auth via session middleware
        const jobInfo = await queryJobInfo(jobID)

        const info = (function (): JobInfo {
            if ('type' in jobInfo && jobInfo.type === 'story') {
                return {
                    type: jobInfo.type,
                    ctime: jobInfo.ctime ?? undefined,
                    mtime: jobInfo.mtime,
                    owner: jobInfo.owner.uuid,
                    title: jobInfo.name || '',
                    coverPhoto: jobInfo.cover_id,
                    allow_comments:
                        jobInfo.permissions.allow_comments === undefined ||
                        jobInfo.permissions.allow_comments,
                    /* Default value for allow_comments is true for empty permission object */
                    allow_uploads:
                        jobInfo.permissions.allow_uploads === undefined ||
                        jobInfo.permissions.allow_uploads,
                    /* Default value for allow_uploads is true for empty permission object */
                    isShared: jobInfo.privacy_mode ? jobInfo.privacy_mode === 'shared' : false,
                    last_update:
                        jobInfo.last_update ||
                        -1 /* May be unknown for timeline, only used with albums */,
                    sort_order: jobInfo.attributes?.sort_order,
                    size: jobInfo.size,
                    has_heic: jobInfo.has_heic,
                }
            }

            if ('type' in jobInfo && jobInfo.type === 'share') {
                return {
                    type: 'share',
                    ctime: jobInfo.ctime,
                    mtime: jobInfo.mtime,
                    owner: jobInfo.owner.uuid,
                }
            }

            return {
                type: 'timeline',
                ctime: jobInfo.ctime,
                mtime: jobInfo.mtime,
            }
        })()

        const { uuid, name, email, profile_picture_url } = jobInfo.owner
        dispatch(
            UserInfoWasFetched([
                {
                    userID: uuid,
                    name: name || '',
                    email,
                    profilePicture: profile_picture_url,
                },
            ]),
        )

        // Whenever a story-job is fetched, also fetch the list of contributing users.
        if (currentUserID && 'type' in jobInfo && jobInfo.type === 'story') {
            const isOwnAlbum = jobInfo.owner.uuid === currentUserID
            void fetchContributingUsers(dispatch, jobID, isOwnAlbum ? undefined : currentUserID)
            void getUserDefinedViewMode(dispatch, jobID)
        }

        dispatch(FetchedJobInfo({ job: jobID, info }))

        return { status: JobInfoStatus.FETCHED, jobInfo }
    } catch (err) {
        console.error(err)

        // Handle QueryJobInfoError from queryJobInfo
        if (err instanceof QueryJobInfoError) {
            switch (err.status) {
                case JobInfoStatus.PASSWORD_REQUIRED:
                    dispatch(JobRequiresPassword(jobID))
                    break
                case JobInfoStatus.LOGIN_REQUIRED:
                    dispatch(JobRequiresLoggedInUser(jobID))
                    break
                case JobInfoStatus.NOT_FOUND:
                    dispatch(JobNotFound(jobID))
                    break
                default:
                    dispatch(ErrorWhenFetchingJobInfo(jobID))
            }
            throw new FetchJobInfoError(err.status, err.cause)
        }

        // Unknown error
        dispatch(ErrorWhenFetchingJobInfo(jobID))
        throw new FetchJobInfoError(JobInfoStatus.FETCH_FAILED, err)
    }
}

// =============== IMAGE UTILITIES
export const getObjectURLFromFileID = async (jobID: JobID, fileID: FileID) => {
    const service = getAppService()
    const blob = await service.getFileBlobFromId(jobID, fileID, true)
    return URL.createObjectURL(blob)
}
